import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth, API } from "../App";
import axios from "axios";
import Tree from "react-d3-tree";
import { ArrowLeft, ZoomIn, ZoomOut, Users, Award, IndianRupee, RotateCcw } from "lucide-react";

// Custom node renderer
const renderCustomNode = ({ nodeDatum }) => {
  const rankColors = {
    "One Star": "#64748B",
    "Bronze": "#CD7F32",
    "Silver": "#9CA3AF",
    "Gold": "#D4AF37",
    "Platinum": "#A1A1AA",
    "Diamond": "#60A5FA",
    "Crown": "#F59E0B",
    "Crown Ambassador": "#F59E0B"
  };

  const isVacant = nodeDatum.attributes?.status === "vacant";
  const borderColor = isVacant ? "#E2E8F0" : (rankColors[nodeDatum.attributes?.rank] || "#64748B");
  const fillColor = isVacant ? "#F8FAFC" : "#E65100";

  return (
    <g>
      <circle
        r={30}
        fill={fillColor}
        stroke={borderColor}
        strokeWidth={3}
      />
      <text
        fill={isVacant ? "#94A3B8" : "white"}
        x={0}
        y={5}
        textAnchor="middle"
        style={{ fontSize: "14px", fontWeight: "bold" }}
      >
        {isVacant ? "+" : (nodeDatum.name?.charAt(0) || "?")}
      </text>
      <text
        fill="#1E293B"
        x={0}
        y={55}
        textAnchor="middle"
        style={{ fontSize: "11px", fontWeight: "500" }}
      >
        {nodeDatum.name?.length > 12 ? nodeDatum.name?.slice(0, 12) + "..." : nodeDatum.name}
      </text>
      <text
        fill="#E65100"
        x={0}
        y={70}
        textAnchor="middle"
        style={{ fontSize: "10px", fontWeight: "600" }}
      >
        {nodeDatum.attributes?.member_id || ""}
      </text>
      {!isVacant && (
        <text
          fill="#64748B"
          x={0}
          y={85}
          textAnchor="middle"
          style={{ fontSize: "9px" }}
        >
          {nodeDatum.attributes?.rank || "New"}
        </text>
      )}
    </g>
  );
};

export default function NetworkPage() {
  const [treeData, setTreeData] = useState(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.7);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const res = await axios.get(`${API}/network/tree`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTreeData(res.data);
      } catch (err) {
        console.error("Error:", err);
      }
    };
    fetchTree();
  }, [token]);

  // Handle container resize
  const containerRef = useCallback((node) => {
    if (node !== null) {
      const { width, height } = node.getBoundingClientRect();
      setDimensions({ width, height });
      setTranslate({ x: width / 2, y: 80 });
    }
  }, []);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 1.5));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.3));
  const handleReset = () => {
    setZoom(0.7);
    setTranslate({ x: dimensions.width / 2, y: 80 });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="network-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/achraj_logo.png" 
                alt="Achraj" 
                className="w-10 h-10 rounded-full border-2 border-[#E65100]"
              />
              <div>
                <span className="font-bold text-slate-900">Achraj</span>
                <p className="text-xs text-slate-500">Network View</p>
              </div>
            </Link>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleZoomOut}
                className="border-slate-300 text-slate-600 hover:bg-slate-100"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleZoomIn}
                className="border-slate-300 text-slate-600 hover:bg-slate-100"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleReset}
                className="border-slate-300 text-slate-600 hover:bg-slate-100"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 md:p-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto mb-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-[#E65100] mx-auto mb-2" />
              <p className="text-lg font-bold text-slate-900">{user?.direct_sponsors || 0}</p>
              <p className="text-xs text-slate-500">Direct</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 text-center">
              <Award className="w-6 h-6 text-[#FF8F00] mx-auto mb-2" />
              <p className="text-lg font-bold text-slate-900">{user?.rank?.name || "One Star"}</p>
              <p className="text-xs text-slate-500">Rank</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 text-center">
              <IndianRupee className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-lg font-bold text-slate-900">₹{(user?.total_income || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500">Income</p>
            </CardContent>
          </Card>
        </div>

        {/* Tree View */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <div 
              ref={containerRef}
              className="h-[60vh] md:h-[70vh] w-full"
              style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)" }}
            >
              {treeData && dimensions.width > 0 ? (
                <Tree
                  data={treeData}
                  orientation="vertical"
                  translate={translate}
                  zoom={zoom}
                  nodeSize={{ x: 150, y: 120 }}
                  renderCustomNodeElement={renderCustomNode}
                  pathFunc="step"
                  separation={{ siblings: 1, nonSiblings: 1.5 }}
                  enableLegacyTransitions
                  transitionDuration={300}
                  pathClassFunc={() => "stroke-[#E65100] stroke-2 fill-none opacity-50"}
                  onUpdate={({ translate: t, zoom: z }) => {
                    setTranslate(t);
                    setZoom(z);
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center">
                  {treeData === null ? (
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E65100]"></div>
                  ) : (
                    <div className="text-center">
                      <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600">No network members yet</p>
                      <p className="text-sm text-slate-400 mt-2">Share your referral code to build your team!</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 mt-6 bg-white rounded-xl p-4 border border-slate-200 shadow-sm max-w-2xl mx-auto">
          {[
            { name: "Star", color: "#64748B" },
            { name: "Bronze", color: "#CD7F32" },
            { name: "Silver", color: "#9CA3AF" },
            { name: "Gold", color: "#D4AF37" },
            { name: "Diamond", color: "#60A5FA" },
            { name: "Crown", color: "#F59E0B" },
          ].map((rank) => (
            <div key={rank.name} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: rank.color }}
              />
              <span className="text-xs text-slate-600">{rank.name}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
